import React from "react";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import FormControlLabel from "@mui/material/FormControlLabel";
import Radio from "@mui/material/Radio";
import RadioGroup from "@mui/material/RadioGroup";
import Checkbox from "@mui/material/Checkbox";
import Chip from "@mui/material/Chip";
import TextField from "@mui/material/TextField";
import LinearProgress from "@mui/material/LinearProgress";
import MailOutlineIcon from "@mui/icons-material/MailOutline";
import { OrangeTextButton } from "../../../common/OrangeTextButton";
import { markSiteUnsynced } from "../../../utils/syncStore";
import { fetchAllSiteCameras } from "./SiteAboutPageUtils/handleSetMilestoneMotion";
import { SiteCameras } from "./SiteAboutPageUtils/siteAboutInterfaces";
import {
  BULK_EMAIL_MODES,
  BULK_EMAIL_MODE_DESCRIPTIONS,
  BULK_EMAIL_MODE_LABELS,
  BulkEmailMode,
  BulkEmailResponse,
  bulkEmailErrorMessage,
  isValidEmail,
  mergeEmails,
  parseEmailInput,
  postBulkEmailRecipients,
} from "./SiteAboutPageUtils/bulkEmailRecipients";

type Dispatcher<S> = React.Dispatch<React.SetStateAction<S>>;

export const BULK_EMAIL_BUTTON_TEXT = "Email alert recipients";
export const BULK_EMAIL_SYNC_NOTE =
  "Sync the site to apply. If the site is armed, accept the restart when you sync.";

const SEPARATOR = /[\s,;]/;

const formatList = (emails: string[]) =>
  emails.length ? emails.join(", ") : "None";

/**
 * Site About → cameras toolbar (ENG-2467): add, remove or replace email alert
 * recipients across the site's cameras in one go — for sites onboarded by
 * duplicating one camera. Preview is a dry run of the same bulk endpoint;
 * Apply saves, reloads the camera list and marks the site unsynced when any
 * camera actually changed.
 */
export const BulkEmailRecipientsButton = ({
  siteCameras,
  siteInfo,
  setSiteCameras,
  setSyncNeeded,
}: {
  siteCameras: SiteCameras;
  siteInfo: any;
  setSiteCameras: Dispatcher<SiteCameras>;
  setSyncNeeded: Dispatcher<boolean>;
}) => {
  const cameras = React.useMemo(
    () => siteCameras?.results ?? [],
    [siteCameras]
  );

  const [open, setOpen] = React.useState(false);
  const [mode, setMode] = React.useState<BulkEmailMode>("add");
  const [emails, setEmails] = React.useState<string[]>([]);
  const [draft, setDraft] = React.useState("");
  const [selectedIds, setSelectedIds] = React.useState<number[]>([]);
  const [busy, setBusy] = React.useState<"preview" | "apply" | null>(null);
  const [preview, setPreview] = React.useState<BulkEmailResponse | null>(null);
  const [applied, setApplied] = React.useState<BulkEmailResponse | null>(null);
  const [error, setError] = React.useState<string | null>(null);
  const [refreshFailed, setRefreshFailed] = React.useState(false);

  // What would be sent right now: the chips plus anything still being typed.
  const effectiveEmails = React.useMemo(
    () => mergeEmails(emails, parseEmailInput(draft)),
    [emails, draft]
  );
  const invalidEmails = effectiveEmails.filter((email) => !isValidEmail(email));
  const allSelected =
    cameras.length > 0 && selectedIds.length === cameras.length;
  const canSubmit =
    !busy &&
    effectiveEmails.length > 0 &&
    invalidEmails.length === 0 &&
    selectedIds.length > 0;

  // Any edit makes a shown preview stale.
  const previewKey = `${mode}|${effectiveEmails.join(",")}|${selectedIds.join(
    ","
  )}`;
  React.useEffect(() => {
    setPreview(null);
  }, [previewKey]);

  const openDialog = () => {
    setMode("add");
    setEmails([]);
    setDraft("");
    setSelectedIds(cameras.map((camera) => camera.id));
    setPreview(null);
    setApplied(null);
    setError(null);
    setRefreshFailed(false);
    setOpen(true);
  };

  const close = () => {
    if (busy) return;
    setOpen(false);
  };

  const commitDraft = () => {
    if (!draft.trim()) return;
    setEmails((prev) => mergeEmails(prev, parseEmailInput(draft)));
    setDraft("");
  };

  const handleDraftChange = (value: string) => {
    // Typing a separator turns everything before it into chips.
    if (SEPARATOR.test(value)) {
      const parts = value.split(SEPARATOR);
      const remainder = parts.pop() ?? "";
      const complete = parseEmailInput(parts.join(","));
      setEmails((prev) => mergeEmails(prev, complete));
      setDraft(remainder);
    } else {
      setDraft(value);
    }
  };

  const handlePaste = (event: React.ClipboardEvent<HTMLDivElement>) => {
    // A single-line input drops pasted newlines (joining the addresses), so
    // take the clipboard text ourselves.
    const text = event.clipboardData.getData("text");
    if (!text) return;
    event.preventDefault();
    const pasted = parseEmailInput(`${draft} ${text}`);
    setEmails((prev) => mergeEmails(prev, pasted));
    setDraft("");
  };

  const removeEmail = (email: string) =>
    setEmails((prev) => prev.filter((existing) => existing !== email));

  const toggleCamera = (id: number) =>
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );

  const run = async (dryRun: boolean) => {
    if (!canSubmit) return;
    commitDraft();
    setBusy(dryRun ? "preview" : "apply");
    setError(null);
    try {
      const response = await postBulkEmailRecipients({
        siteId: Number(siteInfo.id),
        // Omit for "all" so the server covers every camera on the site.
        cameraIds: allSelected ? undefined : selectedIds,
        mode,
        emails: effectiveEmails,
        dryRun,
      });
      if (dryRun) {
        setPreview(response);
      } else {
        if (response.changed_count > 0) {
          markSiteUnsynced(String(siteInfo.id));
          setSyncNeeded(true);
          try {
            const refreshed = await fetchAllSiteCameras(siteInfo.id);
            localStorage.setItem("siteCameras", JSON.stringify(refreshed));
            setSiteCameras(refreshed);
          } catch {
            setRefreshFailed(true);
          }
        }
        setApplied(response);
      }
    } catch (err) {
      setError(bulkEmailErrorMessage(err));
    } finally {
      setBusy(null);
    }
  };

  const unchangedCount = preview
    ? preview.results.filter((row) => !row.changed).length
    : 0;

  return (
    <>
      <OrangeTextButton
        disabled={cameras.length === 0}
        onClick={openDialog}
        text={
          <>
            <MailOutlineIcon fontSize="small" />
            &nbsp;{BULK_EMAIL_BUTTON_TEXT}
          </>
        }
      />
      <Dialog
        open={open}
        onClose={close}
        aria-labelledby="bulk-email-dialog-title"
        maxWidth="md"
        fullWidth
      >
        <DialogTitle id="bulk-email-dialog-title" sx={{ fontFamily: "Mulish" }}>
          Email alert recipients
        </DialogTitle>
        <DialogContent>
          {applied ? (
            <div
              className="font-mulish text-[14px] text-actuate-blue"
              data-testid="bulk-email-summary"
            >
              <p className="font-bold">
                Updated {applied.changed_count} camera
                {applied.changed_count === 1 ? "" : "s"}.
              </p>
              {applied.changed_count === 0 && (
                <p className="mt-2 text-[13px] text-[#5B6B86]">
                  Every selected camera already had these recipients.
                </p>
              )}
              {applied.changed_count > 0 && (
                <p className="mt-2 rounded border-l-4 border-actuate-orange bg-[#FFF4E5] px-3 py-2 text-[13px]">
                  {BULK_EMAIL_SYNC_NOTE}
                </p>
              )}
              {refreshFailed && (
                <p className="mt-2 text-[13px] text-[#5B6B86]">
                  The camera list couldn&apos;t be refreshed; reload the page to
                  see the latest camera details.
                </p>
              )}
            </div>
          ) : (
            <div className="font-mulish text-[14px] text-actuate-blue">
              <RadioGroup
                row
                aria-label="Change type"
                name="bulk-email-mode"
                value={mode}
                onChange={(event) => {
                  // Capture eagerly (see CLAUDE.md's setState-updater gotcha).
                  const next = event.target.value as BulkEmailMode;
                  setMode(next);
                }}
              >
                {BULK_EMAIL_MODES.map((option) => (
                  <FormControlLabel
                    key={option}
                    value={option}
                    disabled={!!busy}
                    control={<Radio size="small" color="warning" />}
                    label={
                      <span className="font-mulish text-[14px] font-semibold">
                        {BULK_EMAIL_MODE_LABELS[option]}
                      </span>
                    }
                  />
                ))}
              </RadioGroup>
              <p className="mb-3 text-[13px] text-[#5B6B86]">
                {BULK_EMAIL_MODE_DESCRIPTIONS[mode]}
              </p>

              {emails.length > 0 && (
                <div
                  className="mb-2 flex flex-wrap gap-1"
                  data-testid="bulk-email-chips"
                >
                  {emails.map((email) => {
                    const valid = isValidEmail(email);
                    return (
                      <Chip
                        key={email}
                        label={email}
                        size="small"
                        color={valid ? "default" : "error"}
                        variant={valid ? "filled" : "outlined"}
                        title={valid ? undefined : "Not a valid email address"}
                        data-invalid={valid ? undefined : "true"}
                        onDelete={busy ? undefined : () => removeEmail(email)}
                      />
                    );
                  })}
                </div>
              )}
              <TextField
                id="bulk-email-input"
                fullWidth
                size="small"
                label="Emails"
                placeholder="Type or paste emails, separated by commas, spaces or new lines"
                value={draft}
                disabled={!!busy}
                onChange={(event) => {
                  const value = event.target.value;
                  handleDraftChange(value);
                }}
                onPaste={handlePaste}
                onBlur={commitDraft}
                onKeyDown={(event) => {
                  if (event.key === "Enter") {
                    event.preventDefault();
                    commitDraft();
                  }
                }}
                error={invalidEmails.length > 0}
                helperText={
                  invalidEmails.length > 0
                    ? `Not valid: ${invalidEmails.join(", ")}. Fix or remove ${
                        invalidEmails.length === 1 ? "it" : "them"
                      } to continue.`
                    : " "
                }
              />

              <div className="mt-3 flex items-center justify-between">
                <span className="font-semibold">
                  Cameras ({selectedIds.length} of {cameras.length} selected)
                </span>
                <span>
                  <OrangeTextButton
                    disabled={!!busy || allSelected}
                    onClick={() =>
                      setSelectedIds(cameras.map((camera) => camera.id))
                    }
                    text="Select all"
                  />
                  <OrangeTextButton
                    disabled={!!busy || selectedIds.length === 0}
                    onClick={() => setSelectedIds([])}
                    text="Select none"
                  />
                </span>
              </div>
              <div className="max-h-[180px] overflow-y-auto rounded border border-actuate-grey px-2">
                {cameras.map((camera) => (
                  <div key={camera.id}>
                    <FormControlLabel
                      disabled={!!busy}
                      control={
                        <Checkbox
                          size="small"
                          color="warning"
                          checked={selectedIds.includes(camera.id)}
                          onChange={() => toggleCamera(camera.id)}
                        />
                      }
                      label={
                        <span className="font-mulish text-[13px]">
                          {camera.camera_name}
                        </span>
                      }
                    />
                  </div>
                ))}
              </div>

              {busy && (
                <LinearProgress color="warning" sx={{ mt: 2 }} role="status" />
              )}

              {preview && (
                <div className="mt-3" data-testid="bulk-email-preview">
                  <p className="font-semibold">
                    {preview.changed_count} camera
                    {preview.changed_count === 1 ? "" : "s"} will change
                    {unchangedCount > 0 && ` · ${unchangedCount} no change`}
                  </p>
                  <div className="mt-1 max-h-[220px] overflow-y-auto">
                    <table className="w-full border-collapse text-left text-[13px]">
                      <thead>
                        <tr className="border-b border-actuate-grey">
                          <th className="py-1 pr-2">Camera</th>
                          <th className="py-1 pr-2">Current</th>
                          <th className="py-1">New</th>
                        </tr>
                      </thead>
                      <tbody>
                        {preview.results.map((row) => (
                          <tr
                            key={row.camera_id}
                            data-testid={`bulk-email-row-${row.camera_id}`}
                            data-changed={row.changed ? "true" : "false"}
                            className={
                              row.changed
                                ? "border-b border-actuate-grey bg-[#FFF4E5]"
                                : "border-b border-actuate-grey text-[#5B6B86]"
                            }
                          >
                            <td className="py-1 pr-2 align-top">
                              {row.camera_name}
                            </td>
                            <td className="break-all py-1 pr-2 align-top">
                              {formatList(row.before)}
                            </td>
                            <td className="break-all py-1 align-top">
                              {row.changed ? (
                                <span className="font-semibold">
                                  {formatList(row.after)}
                                </span>
                              ) : (
                                "No change"
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          )}

          {error && (
            <p
              role="alert"
              className="mt-3 font-mulish text-[13px] text-[#D93636]"
            >
              {error}
            </p>
          )}
        </DialogContent>
        <DialogActions>
          {applied ? (
            <OrangeTextButton disabled={false} onClick={close} text="Close" />
          ) : (
            <>
              <OrangeTextButton
                disabled={!!busy}
                onClick={close}
                text="Cancel"
              />
              <OrangeTextButton
                disabled={!canSubmit}
                onClick={() => run(true)}
                text={busy === "preview" ? "Previewing…" : "Preview"}
              />
              <OrangeTextButton
                disabled={!canSubmit}
                onClick={() => run(false)}
                text={busy === "apply" ? "Applying…" : "Apply"}
              />
            </>
          )}
        </DialogActions>
      </Dialog>
    </>
  );
};
